import React, { useCallback } from 'react';
import includes from 'lodash/includes';

import { DateType } from '../../../../../../../types';

import {
  UniversalFilterDateModes,
  UniversalFilterDateOptions,
  UniversalFilterDateFilterState,
  UniversalFilterDateOnFilterStateChange
} from '../../UniversalFilterDate.types';
import { UniversalFilterDateIsOnOrAfterModeOptions } from './UniversalFilterDateIsOnOrAfterMode.types';

import {
  UniversalFilterOptionSelect,
  UniversalFilterOptionSelectOption,
  UniversalFilterOptionSelectOnChange
} from '../../../../helpers/UniversalFilterOptionSelect';

import { UniversalFilterPositiveIntegerInput } from '../../../../helpers/UniversalFilterPositiveIntegerInput';
import { UniversalFilterDateInput } from '../../../../helpers/UniversalFilterDateInput';

import { filtersKeys } from '../../../../../../../locales/keys';

const options: UniversalFilterOptionSelectOption<UniversalFilterDateIsOnOrAfterModeOptions>[] =
  [
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.EXACT_DAY,
      i18nText: filtersKeys.dateOptions.exactDay
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.TODAY,
      i18nText: filtersKeys.dateOptions.today
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.TOMORROW,
      i18nText: filtersKeys.dateOptions.tomorrow
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.YESTERDAY,
      i18nText: filtersKeys.dateOptions.yesterday
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.ONE_WEEK_AGO,
      i18nText: filtersKeys.dateOptions.oneWeekAgo
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.ONE_WEEK_FROM_NOW,
      i18nText: filtersKeys.dateOptions.oneWeekFromNow
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.ONE_MONTH_AGO,
      i18nText: filtersKeys.dateOptions.oneMonthAgo
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.ONE_MONTH_FROM_NOW,
      i18nText: filtersKeys.dateOptions.oneMonthFromNow
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.NUMBER_OF_DAYS_AGO,
      i18nText: filtersKeys.dateOptions.numberOfDaysAgo
    },
    {
      value: UniversalFilterDateIsOnOrAfterModeOptions.NUMBER_OF_DAYS_FROM_NOW,
      i18nText: filtersKeys.dateOptions.numberOfDaysFromNow
    }
  ];

const currentMode = UniversalFilterDateModes.IS_ON_OR_AFTER;

interface UniversalFilterDateIsOnOrAfterModeProps {
  filterState: UniversalFilterDateFilterState;
  autoFocus?: boolean;
  onFilterStateChange: UniversalFilterDateOnFilterStateChange;
}

function UniversalFilterDateIsOnOrAfterMode({
  filterState,
  autoFocus,
  onFilterStateChange
}: UniversalFilterDateIsOnOrAfterModeProps) {
  const handleChange = useCallback<
    UniversalFilterOptionSelectOnChange<UniversalFilterDateIsOnOrAfterModeOptions>
  >(
    (value) => {
      onFilterStateChange({ mode: currentMode, option: value });
    },
    [onFilterStateChange]
  );

  const handleDaysChange = useCallback<(value?: number) => void>(
    (value) => {
      onFilterStateChange({ ...filterState, optionDays: value });
    },
    [filterState, onFilterStateChange]
  );

  const handleDateChange = useCallback<(value?: DateType) => void>(
    (value) => {
      onFilterStateChange({ ...filterState, optionDate: value });
    },
    [filterState, onFilterStateChange]
  );

  const withDaysInput = includes(
    [
      UniversalFilterDateIsOnOrAfterModeOptions.NUMBER_OF_DAYS_AGO,
      UniversalFilterDateIsOnOrAfterModeOptions.NUMBER_OF_DAYS_FROM_NOW
    ],
    filterState.option
  );

  return (
    <>
      <div className="flex-1">
        <UniversalFilterOptionSelect<UniversalFilterDateOptions>
          options={options}
          selectedValue={filterState.option}
          onChange={handleChange}
        />
      </div>

      {filterState.option ===
      UniversalFilterDateIsOnOrAfterModeOptions.EXACT_DAY ? (
        <UniversalFilterDateInput
          key={[filterState.mode, filterState.option].join('-')}
          value={filterState.optionDate}
          autoFocus={withDaysInput ? false : autoFocus}
          onChange={handleDateChange}
        />
      ) : null}

      {withDaysInput ? (
        <UniversalFilterPositiveIntegerInput
          key={[filterState.mode, filterState.option].join('-')}
          value={filterState.optionDays}
          i18nPlaceholder={filtersKeys.enterDays}
          autoFocus={autoFocus}
          onChange={handleDaysChange}
        />
      ) : null}
    </>
  );
}

export default UniversalFilterDateIsOnOrAfterMode;
